import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { KeyboardArrowLeft, KeyboardArrowRight, Speed as SpeedIcon, ViewList } from "@mui/icons-material";
import { Box, IconButton, List, ListItem, ListItemButton, ListItemText } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { RangeType, useAsu } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";

import { useSpeed } from "../../../../context/speed-context";
import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { SELECTED_ITEM_TYPE, getSelectedItemType } from "../../../../utils";
import { MultiAnalysisPanel } from "../multi-analysis/components/panel";
import {
  MultiAnalysisPanelResizeHandle,
  useMultiAnalysisMainPanelsLayout,
} from "../multi-analysis/hooks/use-multi-analysis-main-panels-layout";
import { getMultiAnalysisMainPanelSx } from "../multi-analysis/utils/main-panels-layout";
import { useTrainRotationDirection } from "../multi-analysis/hooks/use-train-rotation-direction";
import { ROTATION_DIRECTION } from "../multi-analysis/types";
import { SpeedToolbar } from "./components/toolbar";
import { SpeedTimePanel } from "./components/time-panel";
import { SpeedGraphicArea } from "./components/graphic-area";
import {
  SPEED_SCREEN_FORMS,
  SPEED_SCREEN_FORM_DRAG_TYPE,
  useSpeedScreenForms,
  type SpeedScreenForm,
} from "./hooks/use-speed-screen-forms";
import { SPEED_SCREEN_FORM_STRATEGIES } from "./screen-forms/registry";
import type { ISpeedScreenFormStrategiesPull } from "./screen-forms/types";

export const Speed: FC<{ strategies?: ISpeedScreenFormStrategiesPull }> = ({
  strategies: strategiesPull = SPEED_SCREEN_FORM_STRATEGIES,
}) => {
  const { formatMessage } = useIntl();
  const { isLoading, selectedItem, userSelectedRangeType, startTimestamp, endTimestamp } = useAsu();
  const { getSpeedScreenFormLayout } = useSettings();
  const layout = getSpeedScreenFormLayout();
  const [screenFormsCollapsed, setScreenFormsCollapsed] = useState(false);
  const [draggedForm, setDraggedForm] = useState<SpeedScreenForm | null>(null);
  const isTrainSelected = getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.train;
  const trainId = selectedItem && "TrainId" in selectedItem ? Number(selectedItem.TrainId) : null;
  const { toolbarState, setToolbarState } = useSpeed();
  const {
    lockedToolbarDirection,
    isRotationDirectionLocked,
    isLoading: isTrainRotationDirectionLoading,
  } = useTrainRotationDirection({
    enabled: isTrainSelected,
    trainId,
  });
  const rotationDirectionTrainIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isTrainSelected || trainId === null) {
      rotationDirectionTrainIdRef.current = null;
      return;
    }

    const trainChanged = rotationDirectionTrainIdRef.current !== trainId;
    rotationDirectionTrainIdRef.current = trainId;
    if (isTrainRotationDirectionLoading) {
      if (trainChanged && toolbarState.rotationDirection !== ROTATION_DIRECTION.UNDEFINED) {
        setToolbarState({ rotationDirection: ROTATION_DIRECTION.UNDEFINED });
      }
      return;
    }
    if (lockedToolbarDirection === null && !trainChanged) return;

    const nextRotationDirection = lockedToolbarDirection ?? ROTATION_DIRECTION.UNDEFINED;
    if (toolbarState.rotationDirection !== nextRotationDirection) {
      setToolbarState({ rotationDirection: nextRotationDirection });
    }
  }, [
    isTrainRotationDirectionLoading,
    isTrainSelected,
    lockedToolbarDirection,
    setToolbarState,
    toolbarState.rotationDirection,
    trainId,
  ]);
  const screenFormStrategies = useMemo(() => SPEED_SCREEN_FORMS.map(id => new strategiesPull[id]()), [strategiesPull]);
  const screenForms = useSpeedScreenForms(layout, trainId);
  const activeFormId =
    screenForms.activeWindowId !== null ? screenForms.windows[screenForms.activeWindowId]?.form ?? null : null;
  const globalRange = useMemo(() => ({ start: startTimestamp, end: endTimestamp }), [startTimestamp, endTimestamp]);
  const {
    panelsContainerRef,
    panelFlexGrow,
    panelHeights,
    hasMeasuredLayout,
    handleTimeGraphicResizeDelta,
    handleResetPanelProportions,
  } = useMultiAnalysisMainPanelsLayout({
    enabled: isTrainSelected,
    trendPanelCollapsed: true,
    trendPanelVisible: false,
    storageKey: "sp.speed.mainPanelFlexGrow",
  });

  return (
    <ProgressOverlay
      isLoading={isLoading && userSelectedRangeType !== RangeType.RECORDER_MODE}
      wrapperSx={{ height: "100%", minHeight: 0 }}
    >
      {!isTrainSelected ? (
        <EmptyPlaceholder
          icon={<SpeedIcon />}
          text={formatMessage({ id: "pages.speed.stationPlaceholder" })}
          wrapperProps={{ sx: { minHeight: "100%", backgroundColor: "#fafaff" } }}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: screenFormsCollapsed
              ? "minmax(0, 1fr) 42px"
              : { xs: "minmax(0, 1fr) 180px", lg: "minmax(0, 1fr) 260px" },
            gap: 1,
            p: { xs: 1, md: 1.5 },
            bgcolor: "#fafaff",
            overflow: "hidden",
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <MultiAnalysisPanel hideHeader sx={{ flex: "0 0 auto", bgcolor: "#fafaff" }}>
              <SpeedToolbar activeFormId={activeFormId} isRotationDirectionLocked={isRotationDirectionLocked} />
            </MultiAnalysisPanel>
            <Box
              ref={panelsContainerRef}
              sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              <MultiAnalysisPanel
                hideHeader
                sx={getMultiAnalysisMainPanelSx({ panel: "time", hasMeasuredLayout, panelHeights, panelFlexGrow })}
              >
                {trainId !== null ? (
                  <SpeedTimePanel
                    trainId={trainId}
                    globalRange={globalRange}
                    strategies={screenFormStrategies}
                    activeFormId={activeFormId}
                    placedFormIds={screenForms.windows.flatMap(window => (window.form ? [window.form] : []))}
                  />
                ) : null}
              </MultiAnalysisPanel>
              <MultiAnalysisPanelResizeHandle
                onResizeDelta={handleTimeGraphicResizeDelta}
                onDoubleClickReset={handleResetPanelProportions}
              />
              <MultiAnalysisPanel
                hideHeader
                sx={{
                  ...getMultiAnalysisMainPanelSx({
                    panel: "graphic",
                    hasMeasuredLayout,
                    panelHeights,
                    panelFlexGrow,
                  }),
                  border: 0,
                  bgcolor: "transparent",
                }}
              >
                <SpeedGraphicArea
                  layout={layout}
                  strategies={screenFormStrategies}
                  screenForms={screenForms}
                  draggedForm={draggedForm}
                  onDragEnd={() => setDraggedForm(null)}
                />
              </MultiAnalysisPanel>
            </Box>
          </Box>
          <MultiAnalysisPanel
            titleId={screenFormsCollapsed ? undefined : "pages.speed.screenForms.title"}
            icon={screenFormsCollapsed ? undefined : <ViewList fontSize="small" />}
            headerSx={screenFormsCollapsed ? { px: 0.5 } : undefined}
            actions={
              <IconButton
                size="small"
                aria-label={formatMessage({
                  id: screenFormsCollapsed ? "pages.speed.screenForms.expand" : "pages.speed.screenForms.collapse",
                })}
                aria-expanded={!screenFormsCollapsed}
                onClick={() => setScreenFormsCollapsed(value => !value)}
              >
                {screenFormsCollapsed ? (
                  <KeyboardArrowLeft fontSize="small" />
                ) : (
                  <KeyboardArrowRight fontSize="small" />
                )}
              </IconButton>
            }
          >
            {screenFormsCollapsed ? null : (
              <List
                disablePadding
                aria-label={formatMessage({ id: "pages.speed.screenForms.title" })}
                sx={{ height: "100%", overflowY: "auto" }}
              >
                {screenFormStrategies.map(form => {
                  const selected = screenForms.windows.some(window => window.form === form.id);
                  return (
                    <ListItem key={form.id} divider disablePadding>
                      <ListItemButton
                        component="button"
                        selected={selected}
                        aria-pressed={selected}
                        draggable={!selected}
                        onDoubleClick={() => screenForms.toggleForm(form.id)}
                        onKeyDown={event => {
                          if (!event.repeat && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            screenForms.toggleForm(form.id);
                          }
                        }}
                        onDragStart={event => {
                          if (selected) {
                            event.preventDefault();
                            return;
                          }
                          event.dataTransfer.setData(SPEED_SCREEN_FORM_DRAG_TYPE, form.id);
                          event.dataTransfer.effectAllowed = "copy";
                          setDraggedForm(form.id);
                        }}
                        onDragEnd={() => setDraggedForm(null)}
                        sx={{ width: "100%", textAlign: "left", cursor: selected ? "pointer" : "grab" }}
                      >
                        <ListItemText
                          primary={formatMessage({ id: form.titleId })}
                          primaryTypographyProps={{ fontSize: 13 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </MultiAnalysisPanel>
        </Box>
      )}
    </ProgressOverlay>
  );
};
